import { EventEmitter } from '@spotify-internal/emitter';
import { logging } from '@js-sdk/debug-tools';
import { Event } from '../enums/event';
import { defer } from './defer';
const debugLogger = logging.forTag('playback.content_buffer');
/**
 * Acceptable time to get the next fragment before reporting it as a stall.
 */
const STALL_THRESHOLD = 9000;
/**
 * The amount of playback time needed to be buffered at any given time, in
 * seconds.
 */
var RequiredBufferedTime;
(function (RequiredBufferedTime) {
    RequiredBufferedTime[RequiredBufferedTime["AUDIO"] = 10] = "AUDIO";
    RequiredBufferedTime[RequiredBufferedTime["VIDEO"] = 20] = "VIDEO";
})(RequiredBufferedTime || (RequiredBufferedTime = {}));
/**
 * Convert seconds to milliseconds.
 *
 * @param seconds - The number of seconds.
 * @return The value converted to milliseconds.
 */
function _secToMS(seconds) {
    return Math.ceil(seconds * 1000);
}
/**
 * Returns the HTMLMediaElement's buffered range for a particular time.
 *
 * @param mediaSource - The MediaSource.
 * @param time - The time in milliseconds.
 * @return The timerange or null if the sourceBuffers is empty.
 */
function _getBufferedFor(mediaSource, time) {
    const timeRanges = mediaSource.sourceBuffers &&
        mediaSource.sourceBuffers.length > 0 &&
        mediaSource.sourceBuffers[0].buffered;
    if (timeRanges) {
        let timeRangeStart;
        let timeRangeEnd;
        for (let i = 0; i < timeRanges.length; i++) {
            timeRangeStart = timeRanges.start(i);
            timeRangeEnd = timeRanges.end(i);
            if (timeRangeStart <= time && time <= timeRangeEnd) {
                return { start: timeRangeStart, end: timeRangeEnd };
            }
        }
    }
    return null;
}
/**
 * A noop function that is used for catching and ignoring errors that are
 * already handled by the events.
 */
function _ignoreEmittedErrors() { }
/**
 * The ContentBuffer is responsible for managing sourcebuffers and media
 * sources for the Player.
 */
export class ContentBuffer extends EventEmitter {
    /**
     * The ContentBuffer constructor.
     *
     * @param tracker - Tracker instance used in the buffer.
     * @param codecs - A list containing the buffers active codecs for the new
     *     ContentBuffer.
     * @param options - The options for this instance.
     */
    constructor(tracker, codecs = [], options = {}) {
        super();
        /**
         * The MediaSource object.
         */
        this._mediaSource = null;
        /**
         * .A list containing the sourcebuffers.
         */
        this._sourceBuffers = {};
        /**
         * A list containing fragments that are to be appended.
         */
        this._appendingFragments = [];
        /**
         * A list containing queued updates.
         */
        this._updateQueue = [];
        /**
         * A list containing the buffers active codecs.
         */
        this._codecs = [];
        /**
         * The current tracks playId.
         */
        this._playId = 0;
        /**
         * The time when the buffer was last cleared.
         */
        this._lastBufferClearTime = Date.now();
        this._codecs = codecs;
        this._tracker = tracker;
        this._disableBufferAbort = !!options.disableBufferAbort;
        // bind event handlers to the instance scope.
        this._onSourceOpen = this._onSourceOpen.bind(this);
        this._onSourceClose = this._onSourceClose.bind(this);
        this.dequeueUpdates = this.dequeueUpdates.bind(this);
        if (!options.noInit) {
            this._init();
        }
    }
    /**
     * Creates a new ContentBuffer.
     *
     * @param tracker - The tracker to use with the buffer.
     * @param codecs - A list containing the buffers active codecs for the new
     *     ContentBuffer.
     * @param options - The options for this instance.
     * @return A new ConentBuffer instance.
     */
    static create(tracker, codecs = [], options = {}) {
        return new ContentBuffer(tracker, codecs, options);
    }
    /**
     * Initialize the buffer.
     */
    _init() {
        this._mediaSource = new MediaSource();
        this._mediaSource.addEventListener(Event.MEDIA_SOURCE_OPEN, this._onSourceOpen);
        this._mediaSource.addEventListener(Event.MEDIA_SOURCE_CLOSE, this._onSourceClose);
        this._sourceBuffers = {};
    }
    /**
     * Handle sourceopen event on the mediasource.
     */
    _onSourceOpen() {
        debugLogger.info('MediaSource opened.');
        const mediaSource = this._mediaSource;
        if (mediaSource &&
            mediaSource.sourceBuffers.length === 0 &&
            mediaSource.readyState === 'open') {
            debugLogger.info('Creating source buffers for codecs', this._codecs);
            // No active source buffers
            for (let i = 0, len = this._codecs.length; i < len; i++) {
                this._addSourceBuffer(this._codecs[i]);
            }
        }
        // Dequeue any operations that might have been queued while the source
        // buffer was not ready.
        this.dequeueUpdates();
        this.emit(Event.BUFFER_SOURCE_OPEN, null);
    }
    /**
     * Handle sourceclose event on the mediasource.
     */
    _onSourceClose() {
        debugLogger.info('MediaSource closed.');
        this._destroySourceBuffers();
        this.emit(Event.BUFFER_SOURCE_CLOSE, null);
    }
    /**
     * Remove an "appending" fragment from the list.
     *
     * @param fragment - The fragment to remove.
     */
    _removeAppendingFragment(fragment) {
        if (!fragment) {
            return;
        }
        // Mark the chunk as appended.
        const appendingFragments = this._appendingFragments;
        const index = appendingFragments.indexOf(fragment);
        if (index !== -1) {
            appendingFragments.splice(index, 1);
        }
    }
    /**
     * Add a source buffer to the mediasource.
     *
     * @param codec - The codec of the SourceBuffer.
     */
    _addSourceBuffer(codec) {
        if (this._mediaSource) {
            const sourceBuffer = this._mediaSource.addSourceBuffer(codec);
            sourceBuffer.addEventListener(Event.SOURCE_BUFFER_UPDATE_END, this.dequeueUpdates);
            this._sourceBuffers[codec] = sourceBuffer;
        }
    }
    /**
     * Register buffered bytes in tracker.
     *
     * @param bufferData - The BufferSet instance which holds the metadata that
     *     will be tracked.
     */
    _trackBufferingProgress(bufferData) {
        if (bufferData.audio && bufferData.audio.buffer) {
            this._tracker.trackBytesDownloaded(bufferData.audio.buffer.byteLength);
        }
        if (bufferData.video && bufferData.video.buffer) {
            this._tracker.trackBytesDownloaded(bufferData.video.buffer.byteLength);
        }
    }
    /**
     * Given a delay, check if the playback has stalled and notify observers
     * if that's the case
     *
     * @param delay - The time elapsed since the buffering started.
     * @param fragment - The fragment.
     * @param didTimeout - True if a timeout occurred while buffering the
     * fragment.
     */
    _checkStalling(delay, fragment, didTimeout) {
        // note: for video tracks we report the byteStart of the video buffer
        // (since that's likely to be larger).
        const byteStart = fragment.video
            ? fragment.video.byteStart
            : fragment.audio.byteStart;
        if (delay > STALL_THRESHOLD) {
            this.emit(Event.BUFFER_STALLED, {
                byteStart: byteStart,
                timeStart: _secToMS(fragment.timeStart),
                stallAmount: delay - STALL_THRESHOLD,
                didTimeout: didTimeout,
            });
        }
    }
    /**
     * Append the bufferData to a track
     *
     * @param bufferData - The buffered data.
     * @param fragment - The fragment.
     * @param playId - The buffering tracks playId.
     * @param timestamp - An optional timestamp of when the operation started.
     * @return A promise array containing the current in queue Update-promises.
     */
    _appendBufferData(bufferData, fragment, playId, timestamp = Date.now()) {
        this.emit(Event.BUFFERING_END, null);
        const promises = [];
        const audioUpdatePromise = defer();
        this.tryUpdate({
            playId,
            timestamp,
            fragment,
            type: 'append',
            buffer: bufferData.audio.buffer,
            codec: bufferData.audio.codec,
            init: !!fragment.init,
            resolve: audioUpdatePromise.resolve,
        });
        promises.push(audioUpdatePromise.promise);
        if (bufferData.audio.bandwidth !== null) {
            this._tracker.trackBufferURL(bufferData.audio.bufferURL || '', {
                bandwidth: bufferData.audio.bandwidth || 0,
            });
        }
        if (bufferData.video) {
            const videoUpdatePromise = defer();
            this.tryUpdate({
                playId,
                timestamp,
                fragment,
                type: 'append',
                buffer: bufferData.video.buffer,
                codec: bufferData.video.codec,
                init: !!fragment.init,
                resolve: videoUpdatePromise.resolve,
            });
            promises.push(videoUpdatePromise.promise);
            if (bufferData.video.bandwidth !== null) {
                this._tracker.trackBufferURL(bufferData.video.bufferURL || '', {
                    bandwidth: bufferData.video.bandwidth || 0,
                });
            }
        }
        return Promise.all(promises);
    }
    /**
     * Append buffered bytes to a sourcebuffer.
     *
     * @param update - The update containing the buffer to be appended.
     */
    _appendUpdate(update) {
        const sourceBuffer = this._sourceBuffers[update.codec];
        const shouldQueueUpdate = !sourceBuffer || sourceBuffer.updating;
        if (shouldQueueUpdate) {
            this._updateQueue.push(update);
            return;
        }
        // We mark this chunk as no longer appending regardless of whether we succeed
        // or not in appending it so we can retry it if it fails.
        this._removeAppendingFragment(update.fragment);
        try {
            // NOTE: The `buffer` can not be null as `getBufferForFragment` will always return a Buffer.
            sourceBuffer.appendBuffer(update.buffer);
        }
        catch (e) {
            if (e.name === 'QuotaExceededError') {
                this.emitSync(Event.BUFFER_QUOTA_EXCEEDED, null);
            }
            debugLogger.warn('Failed to append buffer', e);
            // Since an error here will not trigger an `updateend`, we should trigger
            // this ourself.
            Promise.resolve()
                .then(this.dequeueUpdates)
                .then(() => {
                // Wait until the dequeueing is fired to ensure that this is not included
                // in the immediate dequeueing.
                if (update.init) {
                    this._updateQueue.push(update);
                    return;
                }
            });
            if (update.init) {
                // Do not resolve yet.
                return;
            }
        }
        if (update.resolve) {
            update.resolve(true);
        }
    }
    /**
     * Cancel an update and perform cleanup routines.
     *
     * @param update - The update operation to cancel.
     */
    _abortUpdate(update) {
        // If it was an append update, remove any operations for it.
        if ('fragment' in update) {
            this._removeAppendingFragment(update.fragment);
        }
        if (update.resolve) {
            update.resolve(false);
        }
        // Make sure to call any other queued updates.
        Promise.resolve().then(this.dequeueUpdates);
    }
    /**
     * Perform an update to the buffer.
     *
     * @param update - The update operation to be executed.
     */
    _processUpdate(update) {
        if (update.type === 'end') {
            this._endUpdate(update);
        }
        else if (update.type === 'duration') {
            this._durationUpdate(update);
        }
        else if (update.type === 'append') {
            this._appendUpdate(update);
        }
    }
    /**
     * Checks whether any of the source buffers for the current MediaSource is in an
     * updating state.
     *
     * @return True is any of the source buffers is appending.
     */
    _isUpdating() {
        if (!this._mediaSource) {
            return false;
        }
        const sourceBuffers = this._mediaSource.sourceBuffers;
        for (let i = 0, l = sourceBuffers.length; i < l; i++) {
            const sourceBuffer = sourceBuffers[i];
            if (sourceBuffer && sourceBuffer.updating) {
                return true;
            }
        }
        return false;
    }
    /**
     * Perform the update to end the stream.
     *
     * @param update - The update operation to be executed.
     */
    _endUpdate(update) {
        // endOfStream can only be called if all of the source buffers are not in an
        // updating state.
        if (this._isUpdating()) {
            this._updateQueue.push(update);
        }
        else if (this._mediaSource && this._mediaSource.readyState === 'open') {
            this._mediaSource.endOfStream();
            if (update.resolve) {
                update.resolve(true);
            }
        }
    }
    /**
     * Sets the duration of the MediaSource.
     *
     * @param update - The update operation to be executed.
     */
    _durationUpdate(update) {
        if (this._isUpdating()) {
            this._updateQueue.push(update);
        }
        else {
            if (this._mediaSource) {
                this._mediaSource.duration = update.duration;
            }
            if (update.resolve) {
                update.resolve(true);
            }
        }
    }
    /**
     * Destroys the source buffers in the current MediaSource.
     */
    _destroySourceBuffers() {
        const mediaSource = this._mediaSource;
        if (!mediaSource) {
            return;
        }
        const sourceBuffers = mediaSource.sourceBuffers;
        if (sourceBuffers.length) {
            for (let i = 0, l = sourceBuffers.length; i < l; i++) {
                const sourceBuffer = sourceBuffers[i];
                sourceBuffer.removeEventListener('updateend', this.dequeueUpdates);
                try {
                    mediaSource.removeSourceBuffer(sourceBuffer);
                    debugLogger.info('Removed source buffer');
                }
                catch (e) {
                    debugLogger.warn('Failed to remove sourcebuffer', e);
                }
            }
        }
        this._sourceBuffers = {};
    }
    /**
     * Marks the stream as ended.
     */
    _endOfStream() {
        if (this._mediaSource && this._mediaSource.readyState !== 'ended') {
            this.tryUpdate({
                playId: this._playId,
                timestamp: Date.now(),
                type: 'end',
            });
        }
    }
    /**
     * Handles an "updateend" event from the SourceBuffer
     */
    dequeueUpdates() {
        const firstItem = this._updateQueue.shift();
        if (firstItem) {
            this.tryUpdate(firstItem);
        }
    }
    /**
     * Append a fragment to a track
     * Adds a fragment to the list of appending fragments, fetches the bytes for
     * it and tries to update the buffer with them.
     *
     * @param content - The track that owns the fragment.
     * @param fragment - The fragment.
     * @param wasSeeking - True if the player was seeking at the point where the
     * fragment was needed.
     * @return A promise array containing the current in queue Update-promises.
     */
    appendFragment(content, fragment, wasSeeking = false) {
        if (this._appendingFragments.indexOf(fragment) !== -1) {
            // We're already appending this fragment, so return early.
            return Promise.resolve([]);
        }
        this._appendingFragments.push(fragment);
        this.emit(Event.BUFFERING_START, null);
        const timeStart = Date.now();
        const playId = this._playId;
        const trackLogJSON = content.toLogJSON();
        return content
            .getBufferForFragment(fragment)
            .then((bufferSet) => {
            this._trackBufferingProgress(bufferSet);
            if (playId !== this._playId) {
                debugLogger.info('Append fragment dropped: playId has changed.');
                return [];
            }
            if (!wasSeeking) {
                this._checkStalling(Date.now() - timeStart, fragment, false);
            }
            return this._appendBufferData(bufferSet, fragment, playId, timeStart);
        })
            .catch((e) => {
            this._removeAppendingFragment(fragment);
            if (!wasSeeking) {
                this._checkStalling(Date.now() - timeStart, fragment, true);
            }
            this.emit(Event.BUFFER_APPEND_ERROR, {
                error: e,
                canPlayNext: true,
                track: trackLogJSON,
            });
            return Promise.reject(e);
        });
    }
    /**
     * Aborts the SourceBuffers of a MediaSource.
     *
     * @param removeBuffered - If true, the operation will also clear any
     * buffered segments.
     */
    abort(removeBuffered = false) {
        if (this._disableBufferAbort) {
            debugLogger.info('Buffer abort skipped; action disabled.');
            return;
        }
        this._lastBufferClearTime = Date.now();
        if (this._mediaSource) {
            const sourceBuffers = this._mediaSource.sourceBuffers;
            for (let i = 0, len = sourceBuffers.length; i < len; i++) {
                if (this._mediaSource.readyState === 'open') {
                    const sourceBuffer = sourceBuffers[i];
                    try {
                        sourceBuffer.abort();
                        const buffered = sourceBuffer.buffered;
                        if (removeBuffered && buffered.length) {
                            const start = buffered.start(0);
                            const end = buffered.end(buffered.length - 1);
                            sourceBuffer.remove(start, end);
                            debugLogger.info('Cleared buffer range', start, end);
                        }
                    }
                    catch (e) {
                        debugLogger.warn('ACMEBuffer.clear failed.', e);
                    }
                }
            }
        }
        this._appendingFragments = [];
    }
    /**
     * @return The mediasource of the ACMEBuffer instance.
     */
    getMediaSource() {
        return this._mediaSource;
    }
    /**
     * Tries to perform an update from the source buffer.
     *
     * If the update cannot be performed (such as when there's another update being
     * applied), the update is queued.
     *
     * If the updates' `playId` field is not the same with the ACMEPlayer's current
     * _playId value, the update is marked as stale and is ignored.
     *
     * @param update - The update operation to perform.
     */
    tryUpdate(update) {
        if (!update) {
            return;
        }
        if (update.playId !== this._playId) {
            debugLogger.info('Try update dropped: playId has changed');
            this._abortUpdate(update);
            return;
        }
        if (update.timestamp < this._lastBufferClearTime) {
            debugLogger.info('Try update dropped: update is older than the last clear');
            this._abortUpdate(update);
            return;
        }
        const mediaSource = this._mediaSource;
        const shouldQueueUpdate = mediaSource &&
            mediaSource.readyState !== 'open' &&
            mediaSource.readyState !== 'ended';
        if (shouldQueueUpdate) {
            this._updateQueue.push(update);
        }
        else {
            this._processUpdate(update);
        }
    }
    /**
     * Remove references to and from mediasource and sourcebuffers.
     */
    destroy() {
        const mediaSource = this._mediaSource;
        if (!mediaSource) {
            return;
        }
        debugLogger.info('Source destroyed');
        mediaSource.removeEventListener('sourceopen', this._onSourceOpen);
        mediaSource.removeEventListener('sourceclose', this._onSourceClose);
        this._destroySourceBuffers();
        this._mediaSource = null;
    }
    /**
     * Progress handler.
     *
     * @param content - The playing track.
     * @param isSeeking - True if the player is seeking when this function
     * is called.
     * @param time - The current progress in seconds.
     */
    progress(content, isSeeking, time) {
        const seekThreshold = 5;
        const mediaSource = this._mediaSource;
        if (!mediaSource) {
            return;
        }
        const isVideo = content.getMediaType() === 'video';
        const requiredBuffered = isVideo
            ? RequiredBufferedTime.VIDEO
            : RequiredBufferedTime.AUDIO;
        if (mediaSource.readyState === 'open' ||
            mediaSource.readyState === 'ended') {
            let fragment;
            let endTime;
            const buffered = _getBufferedFor(mediaSource, time);
            if (!buffered) {
                // Nothing is buffered for this position, insert the segment.
                fragment = content.getFragmentForTime(time);
                if (fragment) {
                    this.appendFragment(content, fragment, isSeeking).catch(_ignoreEmittedErrors);
                    // Check if there's enough time to receive the next progress event
                    // before the appended fragment ends. If it's less than 5 seconds, then
                    // we immediately queue the next segment.
                    if (fragment.timeEnd < time + seekThreshold) {
                        endTime = isVideo ? Math.floor(time) : time;
                        const nextFragment = content.getFragmentAfterTime(endTime);
                        if (nextFragment) {
                            this.appendFragment(content, nextFragment, isSeeking).catch(_ignoreEmittedErrors);
                        }
                        else {
                            // There wasn't any fragment, so end the stream.
                            this._endOfStream();
                        }
                    }
                }
                else {
                    // There's no fragment for this position. It's most likely beyond the
                    // playable range, so let's end the stream.
                    this._endOfStream();
                }
            }
            else {
                if (time + requiredBuffered > mediaSource.duration) {
                    if (mediaSource.readyState === 'open') {
                        this._endOfStream();
                    }
                    return;
                }
                const fragmentLength = content.getFragmentLength();
                // Can we play the current fragment to the end?
                const requiredSegments = Math.ceil(time / fragmentLength);
                const bufferedSegments = Math.floor(buffered.end / fragmentLength);
                if (requiredSegments > bufferedSegments) {
                    // We won't be able to play through the entire fragment, so we need to
                    // reinject from the current position.
                    fragment = content.getFragmentForTime(time);
                    if (fragment) {
                        this.appendFragment(content, fragment, isSeeking).catch(_ignoreEmittedErrors);
                    }
                }
                // We have some stuff buffered, check if we have enough to play at least
                // 10 seconds in the future.
                const playableRemaining = Math.floor(buffered.end - time);
                if (playableRemaining > requiredBuffered) {
                    // We have enough buffered.
                    return;
                }
                const needToFill = Math.min(content.getFragmentLength(), requiredBuffered - playableRemaining);
                if (needToFill < 1) {
                    // How much is missing? We only need to fill if there's more than a
                    // second missing right now, because it'll grow for the next progress()
                    // call anyway.
                    return;
                }
                // We always floor in order to compensate for times when we have missing
                // floating point time frames in the buffer.
                endTime = Math.floor(buffered.end + needToFill);
                fragment = content.getFragmentForTime(endTime);
                if (fragment) {
                    this.appendFragment(content, fragment, isSeeking).catch(_ignoreEmittedErrors);
                }
                else {
                    // There's no fragment after the current one, so end the stream.
                    this._endOfStream();
                }
            }
        }
    }
    /**
     * Recreate the buffer by detroying current mediasource and sourcebuffers, and
     * re-initializing the ACMEBuffer.
     *
     * @param codecs - A list containing the buffers active codecs for the new
     *     ACMEBuffer.
     */
    recreate(codecs) {
        this.abort();
        this.destroy();
        this._codecs = codecs;
        this._init();
    }
    /**
     * Updates the duration of the mediasource.
     *
     * @param duration - The duration.
     */
    setDuration(duration) {
        this.tryUpdate({
            playId: this._playId,
            timestamp: Date.now(),
            type: 'duration',
            duration: duration,
        });
    }
    /**
     * Updates the playId of the ACMEBuffer.
     *
     * @param playId - The play id.
     */
    setPlayId(playId) {
        this._playId = playId;
    }
}
//# sourceMappingURL=buffer.js.map